import { OptionallyVisible, PageWrapper } from 'components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCancelCryptoPayoutMutation } from 'services/cryptoPayouts';

import { LoadingIndicator } from 'components/loadingIndicator/loadingIndicator';

import { ErrorText } from 'pages/error/styles';

import { CryptoPayout, CryptoPayoutPaymentMethod, CryptoPayoutStatus } from 'types/cryptoPayout';
import { ReduxToolkitError, ReduxToolkitResponse } from 'types/http';

import { usePolledCryptoPayoutResponse } from '../utils';
import CancelPayout from './components/cancel';
import { CryptoPayoutOutlineSection } from './components/outline';
import CryptoPayoutInfo from './components/payoutInfo';
import { StatusPayoutInfo } from './components/status';
import { BREADCRUMBS, LABELS } from './keys';
import { Header, StartOverButton } from './styles';

interface AgentDashboardContainerProps {
  uuid: string;
  token: string;
  onStartOver: () => void;
}
const AgentDashboardContainer = ({ uuid, token, onStartOver }: AgentDashboardContainerProps) => {
  const { t } = useTranslation();
  const [isCancelled, setCancelled] = useState(false);
  const breadcrumbs = BREADCRUMBS.map((breadcrumb) => t(breadcrumb));

  if (!uuid) {
    throw new Error('Missing crypto payout uuid');
  }

  const { payoutResponse, isLoading, error } = usePolledCryptoPayoutResponse(uuid);
  const [cancel, { isLoading: isCancelling, error: cancelError }] = useCancelCryptoPayoutMutation();

  const cancelPayout = async () => {
    const cancelResponse = (await cancel({ uuid })) as ReduxToolkitResponse<{ data: CryptoPayout }>;
    if (!('error' in cancelResponse)) {
      setCancelled(true);
    }

    return cancelResponse as ReduxToolkitResponse<{ data: CryptoPayout }>;
  };

  const onStartOverClick = () => {
    setCancelled(false);
    onStartOver();
  };

  if (isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <ErrorText>{t(LABELS.LOADING_ERROR)}</ErrorText>
      </PageWrapper>
    );
  }

  if (isCancelled || payoutResponse?.data?.status === CryptoPayoutStatus.Canceled) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <ErrorText>{t(LABELS.CANCELLED_MESSAGE)}</ErrorText>
        <StartOverButton secondary onClick={onStartOverClick}>
          {t(LABELS.START_OVER)}
        </StartOverButton>
      </PageWrapper>
    );
  }

  if (!payoutResponse?.data) {
    return null;
  }

  const isCancelAvailable = payoutResponse!.data.isCancelable;

  return (
    <PageWrapper breadcrumbs={breadcrumbs}>
      <Header>{t(LABELS.PAGE_TITLE)}</Header>
      <CryptoPayoutOutlineSection
        customerEmail={payoutResponse!.data.customer.email}
        payoutTotal={payoutResponse!.data.fiatAmount}
        status={payoutResponse!.data.status}
      />
      <CryptoPayoutInfo
        token={token}
        cryptoPayoutId={payoutResponse.data.uuid}
        customerStatus={payoutResponse.data.customer.kycStatus}
        payoutStatus={payoutResponse.data.status}
      />
      <StatusPayoutInfo
        status={payoutResponse.data.status}
        statusHistory={payoutResponse.data.statusHistory}
        cryptoCurrencyCode={payoutResponse.data.cryptoCurrencyCode}
        method={payoutResponse.data.method ?? CryptoPayoutPaymentMethod.Onchain}
      />
      <OptionallyVisible visible={isCancelAvailable}>
        <CancelPayout cancel={cancelPayout} isLoading={isCancelling} error={cancelError as ReduxToolkitError | undefined} />
      </OptionallyVisible>
      <StartOverButton secondary onClick={onStartOverClick}>
        {t(LABELS.START_OVER)}
      </StartOverButton>
    </PageWrapper>
  );
};

export default AgentDashboardContainer;
